import { type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/utils/tailwind.util';
import { type AvatarShape, SHAPE_MAP } from '../constants/avatar.constants';

/**
 * 아바타 바깥 프레임 — 모양·보더·배경·hover 트리트먼트.
 *
 * SpriteAvatar · BakedAvatar · FallbackAvatar 가 각자 손으로 쓰던 프레임 문자열을
 * 한 곳으로 모으고, 매직 컬러(`border-[rgba(28,27,31,0.04)]`)를 ZUS 토큰
 * (`border-opacity-assistive`)으로 바꾼다. 안쪽 콘텐츠(스프라이트 합성 · baked
 * 이미지 · 폴백 이미지)는 각 컴포넌트가 계속 소유한다.
 *
 * ZUS `avatarVariants` 를 import 하지 않는 이유:
 * quiz-app 은 `@zep-us/zus@2.1.0` 을 쓰는데 avatar 는 2.2.0 에 들어왔다. 그래서
 * 지금은 같은 토큰 클래스를 직접 나열한다 — 아래 클래스 조합은 2.2.0
 * `avatarVariants` 의 base 에서 hover 트리트먼트만 뺀 것이다. zus 를 올리면 이 문자열을
 * `avatarVariants({ rounded })` 호출로 교체하면 된다.
 *
 * 크기를 받지 않는 이유:
 * ZEP 아바타 스케일(20 · 26 · 30 · 34 · 36 · 96px)이 ZUS(26 · 30 · 36 · 40 · 48 ·
 * 62px)와 어긋나 34 · 20 · 96 이 없다. 호출부가 임의 클래스(`size-[96px]`)나 인라인
 * 스타일로 넘긴다. (`cn` = tailwind-merge 이므로 뒤에 오는 클래스가 이긴다)
 *
 * `overflow-hidden` 이 필요한 이유:
 * 스프라이트는 프레임 밖으로 넘치도록 오프셋을 준 뒤 잘라내는 방식이라 부모의
 * 클리핑이 반드시 있어야 한다.
 */
const AVATAR_FRAME_CLASS = cn('relative inline-flex aspect-square shrink-0 items-center justify-center overflow-hidden', 'border border-opacity-assistive'
// hover 트리트먼트(after: 오버레이)는 ZUS avatarVariants 에 있지만 여기선 뺐다.
// 그라데이션 링(BASIC/PRO) 분기는 이 프레임을 쓰지 않아 FREE 아바타에만 hover 가
// 생기는 비대칭이 되고, 리스트 행처럼 클릭 불가능한 자리의 아바타에도 클릭
// 가능성을 암시하게 된다. zus 2.2 승격 때 디자인 리뷰와 함께 도입한다.
);
export const AvatarFrame = ({
  shape = 'circle',
  backgroundColor,
  className,
  style,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  shape?: AvatarShape;
  backgroundColor?: string;
}) => {
  return <div className={cn(AVATAR_FRAME_CLASS, SHAPE_MAP[shape], className)} style={{
    ...(backgroundColor !== undefined && {
      backgroundColor
    }),
    ...style
  }} {...props} data-sentry-component="AvatarFrame" data-sentry-source-file="AvatarFrame.tsx" />;
};